// src/pages/Clients.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Routes, Route, useParams } from "react-router-dom";
import { FiPlus, FiSearch, FiEdit, FiTrash2, FiUser } from "react-icons/fi";
import { clientService } from "../services/clientService";
import ConfirmationModal from "../components/common/ConfirmationModal";
import { useConfirmation } from "../hooks/useConfirmation";
import toast from "react-hot-toast";
import ClientList from "../components/clients/ClientList";
import ClientForm from "../components/clients/ClientForm";
import ClientProfile from "../components/clients/ClientProfile";
import { useTenant } from "../context/TenantContext";
import { v4 as uuidv4 } from 'uuid';

export default function Clients() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentTenant, loading: tenantLoading } = useTenant();
  const { confirmationState, showConfirmation, handleConfirm, handleCancel } = useConfirmation();
  const [clients, setClients] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // "active", "inactive", "all"

  // Load clients only after tenant context is loaded
  useEffect(() => {
    if (!tenantLoading && currentTenant) {
      loadClients();
    }
  }, [tenantLoading, currentTenant]);

  const loadClients = async () => {
    try {
      const clientsData = await clientService.getAllClients();
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  // Add or update client
  const saveClient = async (client) => {
    try {
      let savedClient;
      if (client.id) {
        savedClient = await clientService.updateClient(client.id, client);
        setClients((cs) =>
          cs.map((c) => (c.id === client.id ? savedClient : c))
        );
      } else {
        const newClient = { ...client, id: uuidv4() };
        savedClient = await clientService.createClient(newClient);
        setClients((cs) => [...cs, savedClient]);
      }
      setShowClientModal(false);
      setEditingClient(null);
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  // Start editing from profile/card
  const startEdit = (c) => {
    setEditingClient(c);
    setShowClientModal(true);
  };

  // Delete a client
  const deleteClient = async (c) => {
    const confirmed = await showConfirmation({
      title: "Delete Client",
      message: `Delete client "${c.name}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger"
    });
    
    if (confirmed) {
      try {
        await clientService.deleteClient(c.id);
        setClients((cs) => cs.filter((x) => x.id !== c.id));
        setEditingClient(null);
        // If user is on that client's profile page, redirect to main list
        if (id === c.id) {
          navigate("/clients");
        }
        toast.success('Client deleted successfully');
      } catch (error) {
        console.error('Error deleting client:', error);
        toast.error('Failed to delete client');
      }
    }
  };

  // Filter for search and status
  const filtered = clients.filter((c) => {
    if (!c || !c.name) return false;
    
    // Search filter
    const matchesSearch = c.name.toLowerCase().includes(searchTerm) ||
                         (c.email && c.email.toLowerCase().includes(searchTerm));
    
    // Status filter
    let matchesStatus = true;
    if (statusFilter === "active") {
      matchesStatus = c.isActive !== false; // Default to active if not set
    } else if (statusFilter === "inactive") {
      matchesStatus = c.isActive === false;
    }
    // "all" shows everything
    
    return matchesSearch && matchesStatus;
  });

  // Modal close handler
  const handleCloseModal = () => {
    setShowClientModal(false);
    setEditingClient(null);
  };

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <div className="space-y-6 p-4">
              <div className="flex items-center justify-end">
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  onClick={() => {
                    setEditingClient(null);
                    setShowClientModal(true);
                  }}
                >
                  + Add Client
                </button>
              </div>

              <div className="mt-4 space-y-4">
                {/* Search Input */}
                <input
                  type="text"
                  placeholder="Search clients…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                  className="w-full p-2 border rounded"
                />
                
                {/* Status Filter */}
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-700">Filter by status:</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setStatusFilter("active")}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        statusFilter === "active"
                          ? "bg-green-100 text-green-800 border border-green-300"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      Active ({clients.filter(c => c.isActive !== false).length})
                    </button>
                    <button
                      onClick={() => setStatusFilter("inactive")}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        statusFilter === "inactive"
                          ? "bg-orange-100 text-orange-800 border border-orange-300"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      Inactive ({clients.filter(c => c.isActive === false).length})
                    </button>
                    <button
                      onClick={() => setStatusFilter("all")}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        statusFilter === "all"
                          ? "bg-blue-100 text-blue-800 border border-blue-300"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      All ({clients.length})
                    </button>
                  </div>
                </div>
              </div>

              <ClientList
                clients={filtered}
                onSelect={(client) => {
                  navigate(`/clients/${client.id}`);
                }}
              />
            </div>
          }
        />

        <Route
          path=":id/*"
          element={
            <ClientProfile
              clients={clients}
              onEdit={startEdit}
              onDelete={deleteClient}
              onBack={() => navigate("/clients")}
            />
          }
        />
      </Routes>

      {/* ---- MODAL always visible for Add/Edit, regardless of route ---- */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg w-full max-w-5xl p-8 relative max-h-[90vh] overflow-y-auto">
            <ClientForm
              initialData={editingClient}
              onSave={saveClient}
              onCancel={handleCloseModal}
            />
            <button
              onClick={handleCloseModal}
              className="absolute top-3 right-4 text-2xl text-gray-400 hover:text-gray-800"
              aria-label="Close modal"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
      />
    </>
  );
}
